import { useRef } from "react";
import { motion } from "framer-motion";
import TiltCard from "@/components/TiltCard";
import Divider from "@/components/Divider";
import { ChevronLeft, ChevronRight } from "lucide-react";

const REAL = [
    {
        slug: "andrie",
        name: "Andrie",
        src: "https://customer-assets.emergentagent.com/job_zero-ads-viral/artifacts/y3bkyf6f_Andrie-254K.jpg",
    },
    {
        slug: "neeka",
        name: "Neeka",
        src: "https://customer-assets.emergentagent.com/job_zero-ads-viral/artifacts/ebcgxt2j_Neeka-704K.jpg",
    },
    {
        slug: "helen",
        name: "Helen",
        src: "https://customer-assets.emergentagent.com/job_zero-ads-viral/artifacts/gqlqngqx_Helen-2320.jpg",
    },
    {
        slug: "lauren",
        name: "Lauren",
        src: "https://customer-assets.emergentagent.com/job_zero-ads-viral/artifacts/izx2sbb4_Lauren-8950.jpg",
    },
    {
        slug: "belle",
        name: "Belle",
        src: "https://customer-assets.emergentagent.com/job_zero-ads-viral/artifacts/pb07cumz_Belle-1880.jpg",
    },
];

export default function RealConversations() {
    const trackRef = useRef(null);

    const scrollBy = (dir) => {
        const el = trackRef.current;
        if (!el) return;
        const card = el.querySelector("[data-card]");
        const step = card ? card.getBoundingClientRect().width + 24 : 400;
        el.scrollBy({ left: dir * step, behavior: "smooth" });
    };

    return (
        <section
            id="real-conversations"
            data-testid="real-conversations-section"
            className="relative py-20 md:py-24"
        >
            <Divider label="unedited" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 md:mb-12">
                    <div className="max-w-2xl">
                        <p className="text-xs uppercase tracking-[0.35em] text-[var(--accent-gold)] mb-5">
                            Real Conversations
                        </p>
                        <h3 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.05] text-[var(--text-primary)]">
                            Raw client screenshots.{" "}
                            <span className="italic text-[var(--accent-champagne)]">
                                Untouched.
                            </span>
                        </h3>
                        <p className="mt-5 text-[var(--text-secondary)] max-w-xl">
                            No edits. No overlays. No stylised UI. The screenshots themselves are
                            the testimonial.
                        </p>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={() => scrollBy(-1)}
                            aria-label="Scroll left"
                            data-testid="real-conv-prev"
                            className="w-11 h-11 rounded-full border border-[var(--border-subtle)] hover:border-[var(--accent-gold)] flex items-center justify-center text-[var(--text-primary)] hover:text-[var(--accent-champagne)] transition-colors"
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button
                            onClick={() => scrollBy(1)}
                            aria-label="Scroll right"
                            data-testid="real-conv-next"
                            className="w-11 h-11 rounded-full border border-[var(--border-subtle)] hover:border-[var(--accent-gold)] flex items-center justify-center text-[var(--text-primary)] hover:text-[var(--accent-champagne)] transition-colors"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>

                <div
                    ref={trackRef}
                    data-testid="real-conv-track"
                    className="flex gap-6 md:gap-8 overflow-x-auto snap-x snap-mandatory pb-8 -mx-4 px-4 sm:mx-0 sm:px-0 perspective-1500"
                    style={{ scrollbarWidth: "none" }}
                >
                    {REAL.map((r, i) => (
                        <motion.div
                            key={r.slug}
                            data-card
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                            className="shrink-0 snap-start w-[280px] sm:w-[320px] md:w-[360px]"
                        >
                            <TiltCard intensity={5} lift={10}>
                                <figure
                                    data-testid={`real-conv-${r.slug}`}
                                    className="rounded-2xl overflow-hidden border border-[var(--border-subtle)] hover:border-[var(--border-accent)] bg-[rgba(0,0,0,0.4)] shadow-[0_25px_50px_-20px_rgba(0,0,0,0.8),0_0_30px_-15px_rgba(212,175,55,0.2)] hover:shadow-[0_30px_60px_-20px_rgba(0,0,0,0.9),0_0_45px_-10px_rgba(139,10,10,0.55)] transition-shadow"
                                >
                                    <img
                                        src={r.src}
                                        alt={`WhatsApp screenshot from ${r.name}`}
                                        loading="lazy"
                                        className="w-full h-auto block object-contain"
                                    />
                                </figure>
                            </TiltCard>
                            <figcaption className="mt-3 text-xs uppercase tracking-[0.3em] text-[var(--text-secondary)]">
                                {r.name}
                            </figcaption>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
